/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Users, Plus, FileText, Folder, Files } from 'lucide-react';
import { DashboardSummaryCards } from '../../components/dashboard/DashboardSummaryCards';
import { UserModal } from '../../components/user/UserModal';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../../services/dashboard.service';
import { FileService } from '../../services/file.service';
import { UserService } from '../../services/user.service';
import { FileUploadModal } from '../../components/file/FileUploadModal';
import { CompanyModal } from '../../components/company/CompanyModal';
import { CompanyType } from '../../types/CompanyType';
import { useAuth } from '../../context/AuthContext';
import { FolderType } from '../../types/FolderType';
import { FolderModal } from '../../components/folder/FolderModal';

export const CompanyAdminDashboard = () => {
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [stats, setStats] = useState({ users: 0, invites: 0, files: 0, folders: 0 });
  const [recentFiles, setRecentFiles] = useState<any[]>([]);
  const [recentInvitations, setRecentInvitations] = useState<any[]>([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<CompanyType | null>(null);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isFilesLoading, setIsFilesLoading] = useState(true);
  const [isInvitesLoading, setIsInvitesLoading] = useState(true);

  const { user } = useAuth();

  const loadStats = async () => {
    try {
      setIsStatsLoading(true);
      const res = await DashboardService.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load dashboard stats', err);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const loadRecentFiles = async () => {
    try {
      setIsFilesLoading(true);
      const res = await FileService.getFiles();
      setRecentFiles(res.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to load recent files:', err);
    } finally {
      setIsFilesLoading(false);
    }
  };

  const loadRecentInvitations = async () => {
    try {
      setIsInvitesLoading(true);
      const res = await UserService.getAllInvitations();
      setRecentInvitations(res.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to load recent invitations:', err);
    } finally {
      setIsInvitesLoading(false);
    }
  };

  const formatRoleName = (roleName: string) => {
    if (!roleName) return '';
    return roleName
      .trim()
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    loadStats();
    loadRecentFiles();
    loadRecentInvitations();
  }, []);

  return (
    <div>
      <DashboardSummaryCards
        cards={[
          { title: 'Users', value: stats.users, icon: <Users /> },
          { title: 'Invitations', value: stats.invites, icon: <Plus /> },
          { title: 'Files', value: stats.files, icon: <FileText /> },
          { title: 'Folders', value: stats.folders, icon: <Folder /> },
        ]}
      />

      <div className="flex flex-col mt-8 lg:flex-row">
        <div className="w-full space-y-10 lg:w-[77%]">
          <div className="sm:w-[85%]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Recent Files</h2>
              <a href="/files" className="text-sm font-medium text-primary hover:underline">
                View All
              </a>
            </div>
            {isFilesLoading ? (
              <p className="text-sm text-muted-foreground text-primary">Loading...</p>
            ) : recentFiles.length > 0 ? (
              <ul className="space-y-3">
                {recentFiles.map((file) => (
                  <li
                    key={file.id}
                    className="p-4 bg-white border rounded-md shadow-sm cursor-pointer hover:bg-gray-100 "
                    onClick={() => {
                      window.open(`/files/${file.id}`, '_blank');
                    }}
                  >
                    <div className="flex items-center justify-between ">
                      <div>
                        <p className="text-sm font-medium text-primary">
                          {file.editedName ? file.editedName : file.fileName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Uploaded by: {file.addedBy?.email || 'N/A'}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(file.uploadedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground text-primary">No recent files to show.</p>
            )}
          </div>

          <div className="sm:w-[85%]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Recent Invitations</h2>
              <a
                href="/company-settings/invitations"
                className="text-sm font-medium text-primary hover:underline"
              >
                View All
              </a>
            </div>
            {isInvitesLoading ? (
              <p className="text-sm text-muted-foreground text-primary">Loading...</p>
            ) : recentInvitations.length > 0 ? (
              <ul className="space-y-3">
                {recentInvitations.map((invite) => (
                  <li key={invite.id} className="p-4 bg-white border rounded-md shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-primary">{invite.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Role: {formatRoleName(invite.role?.name || invite.role)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-sm rounded-full font-medium ${
                          invite.status === 'accepted'
                            ? 'bg-green-100 text-green-700 border'
                            : invite.status === 'revoked'
                              ? 'bg-red-100 text-red-700 border'
                              : invite.status === 'expired'
                                ? 'bg-yellow-100 text-yellow-800 border'
                                : 'bg-blue-100 text-blue-700 border'
                        }`}
                      >
                        {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground text-primary">No invitations found.</p>
            )}
          </div>
        </div>

        <div className="w-full lg:w-[23%]">
          <div>
            <h2 className="mt-6 mb-4 text-lg font-semibold lg:mt-0 text-primary">Quick Actions</h2>
            <div className="space-y-4">
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted"
                onClick={() => {
                  setCurrentCompany(
                    user?.company
                      ? {
                          id: user.company.id,
                          name: user.company.name,
                          country: user.company.country,
                          isActive: user.company.isActive,
                          logo: '',
                        }
                      : null,
                  );

                  setIsEditCompanyOpen(true);
                }}
              >
                <Files className="w-5 h-5" />
                Edit Company Info
              </button>
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted"
                onClick={() => setIsUserModalOpen(true)}
              >
                <Users className="w-5 h-5" />
                Add User
              </button>
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted"
                onClick={() => setIsFileModalOpen(true)}
              >
                <FileText className="w-5 h-5" />
                Upload File
              </button>
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted"
                onClick={() => setIsFolderModalOpen(true)}
              >
                <Folder className="w-5 h-5" />
                Create Folder
              </button>
            </div>
          </div>
        </div>
      </div>

      <UserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        onSuccess={() => {
          loadStats();
          loadRecentInvitations();
          setIsUserModalOpen(false);
        }}
      />

      <FileUploadModal
        isOpen={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        onSuccess={() => {
          loadRecentFiles();
        }}
      />

      <CompanyModal
        isOpen={isEditCompanyOpen}
        onClose={() => setIsEditCompanyOpen(false)}
        onSuccess={() => {}}
        editingCompany={currentCompany}
      />

      <FolderModal
        isOpen={isFolderModalOpen}
        onClose={() => {
          setIsFolderModalOpen(false);
        }}
        onSuccess={loadStats}
      />
    </div>
  );
};
